import React, { useState, useEffect } from 'react';
import { Clock, Package, ChevronDown, ChevronUp, RotateCcw, ShoppingCart } from 'lucide-react';
import { getFrequentOrders } from '../../../services/api/frequentOrderApi';
import ConfirmationModal from '../../../components/common/Modal/ConfirmationModal';

/**
 * POS Frequent Orders — shows the last N orders for the selected customer
 * placed by the current salesperson. Includes a "Reorder" action that
 * populates the cart with the same items.
 */
const PosFrequentOrders = ({ customerPublicId, onReorder, cartHasItems, inline = false }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [reorderTarget, setReorderTarget] = useState(null); // order to reorder (confirmation)

    // Fetch frequent orders when customer changes
    useEffect(() => {
        if (!customerPublicId) {
            setOrders([]);
            return;
        }

        const fetchOrders = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getFrequentOrders(customerPublicId, 5);
                setOrders(data);
            } catch (err) {
                console.error('[PosFrequentOrders] Failed to load:', err);
                setError('Could not load previous orders');
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [customerPublicId]);

    // Don't render if no customer selected
    if (!customerPublicId) return null;

    const handleReorderClick = (order) => {
        if (cartHasItems) {
            // Ask for confirmation before replacing cart
            setReorderTarget(order);
        } else {
            executeReorder(order);
        }
    };

    const executeReorder = (order) => {
        if (!order?.items?.length) return;

        const cartItems = order.items.map(item => ({
            id: item.productPublicId,
            name: item.productName,
            price: item.unitPrice || 0,
            quantity: item.quantity || 1,
            unit: item.unit || 'pcs',
        }));

        onReorder(cartItems);
        setReorderTarget(null);
        setIsOpen(false);
    };

    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered':
            case 'completed':
                return 'bg-emerald-100 text-emerald-700';
            case 'new':
            case 'pending':
                return 'bg-sky-100 text-sky-700';
            case 'processing':
            case 'confirmed':
                return 'bg-amber-100 text-amber-700';
            case 'ready':
                return 'bg-violet-100 text-violet-700';
            default:
                return 'bg-gray-100 text-gray-600';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <div className={inline ? 'relative flex-shrink-0' : ''}>
            {/* Toggle Button */}
            {inline ? (
                // Compact square icon button for inline (customer row) mode
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    title="Frequent Orders"
                    className={`relative flex items-center justify-center w-10 h-10 rounded-xl border transition-all flex-shrink-0 ${
                        isOpen
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                            : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border-gray-200'
                    }`}
                >
                    <Clock className="w-4 h-4" />
                    {orders.length > 0 && (
                        <span className="absolute -top-1 -right-1 bg-indigo-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                            {orders.length}
                        </span>
                    )}
                </button>
            ) : (
                // Full-width toggle button for standalone mode
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isOpen
                            ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Frequent Orders</span>
                        {orders.length > 0 && !isOpen && (
                            <span className="bg-indigo-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                {orders.length}
                            </span>
                        )}
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            )}

            {/* Expandable Panel */}
            {isOpen && (
                <div className={`${
                    inline
                        ? 'absolute right-0 top-full mt-2 w-80 z-50 shadow-xl'
                        : 'mt-2'
                } bg-white border border-gray-200 rounded-xl overflow-hidden`}>
                    {loading ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                            <div className="animate-spin w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full mx-auto mb-2" />
                            Loading previous orders…
                        </div>
                    ) : error ? (
                        <div className="px-4 py-6 text-center text-sm text-red-500">{error}</div>
                    ) : orders.length === 0 ? (
                        <div className="px-4 py-6 text-center">
                            <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500 font-medium">No previous orders</p>
                            <p className="text-xs text-gray-400 mt-0.5">Orders you place for this customer will appear here</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto custom-scrollbar">
                            {orders.map(order => {
                                const isExpanded = expandedOrderId === order.publicId;
                                return (
                                    <div key={order.publicId} className="group">
                                        {/* Order Summary Row */}
                                        <div className="px-3 py-2.5 flex items-center gap-3">
                                            {/* Expand toggle + Info */}
                                            <button
                                                onClick={() => setExpandedOrderId(isExpanded ? null : order.publicId)}
                                                className="flex-1 min-w-0 flex items-center gap-2.5 text-left"
                                            >
                                                <div className="flex-shrink-0">
                                                    {isExpanded
                                                        ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                                                        : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                                                    }
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-bold text-gray-800">
                                                            #{order.orderRefNo || order.publicId?.substring(0, 8)}
                                                        </span>
                                                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${getStatusStyle(order.status)}`}>
                                                            {order.status}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-[11px] text-gray-400">
                                                            {formatDate(order.createdAt)}
                                                        </span>
                                                        <span className="text-[11px] text-gray-400">•</span>
                                                        <span className="text-[11px] text-gray-500 font-medium">
                                                            {order.items?.length || 0} items
                                                        </span>
                                                    </div>
                                                </div>
                                            </button>

                                            {/* Amount + Reorder */}
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <span className="text-sm font-bold text-gray-800">
                                                    AED {Number(order.totalAmount || 0).toFixed(2)}
                                                </span>
                                                <button
                                                    onClick={() => handleReorderClick(order)}
                                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors text-xs font-bold"
                                                    title="Reorder same items"
                                                >
                                                    <RotateCcw className="w-3 h-3" />
                                                    Reorder
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expanded Items */}
                                        {isExpanded && order.items?.length > 0 && (
                                            <div className="bg-gray-50 px-4 py-2 border-t border-gray-100">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="text-gray-400 uppercase tracking-wider">
                                                            <th className="text-left font-medium py-1">Product</th>
                                                            <th className="text-right font-medium py-1">Qty</th>
                                                            <th className="text-right font-medium py-1">Price</th>
                                                            <th className="text-right font-medium py-1">Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {order.items.map((item, idx) => (
                                                            <tr key={idx} className="text-gray-700">
                                                                <td className="py-1.5 pr-2 font-medium truncate max-w-[140px]">
                                                                    {item.productName}
                                                                </td>
                                                                <td className="py-1.5 text-right whitespace-nowrap">
                                                                    {item.quantity} {item.unit || ''}
                                                                </td>
                                                                <td className="py-1.5 text-right whitespace-nowrap">
                                                                    {Number(item.unitPrice || 0).toFixed(2)}
                                                                </td>
                                                                <td className="py-1.5 text-right font-semibold whitespace-nowrap">
                                                                    {Number(item.subtotal || 0).toFixed(2)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Reorder Confirmation Modal (when cart has items) */}
            <ConfirmationModal
                isOpen={!!reorderTarget}
                onClose={() => setReorderTarget(null)}
                onConfirm={() => executeReorder(reorderTarget)}
                title="Replace Cart?"
                message="Your cart already has items. Reordering will replace your current cart with the items from this order."
                confirmText="Yes, Replace Cart"
                cancelText="Cancel"
                type="warning"
                icon={<ShoppingCart className="w-6 h-6 text-amber-500" />}
            />
        </div>
    );
};

export default PosFrequentOrders;
